import { uniq } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { BETA_FEATURE_LLM_EVAL } from '@/lib/betaFeatures';
import { isEmail, isValidEmail } from '@/lib/utils';
import useInviteMembersList from '@API/workspace/useInviteMembersList';
import useWorkspaceMembers from '@API/workspace/useWorkspaceMembers';
import { MultipleAutocomplete } from '@DesignSystem/controllers';
import { NO_ITEMS_STRATEGY } from '@DesignSystem/controllers/MultipleAutocomplete/MultipleAutocomplete';
import { FullWidthBasicModal } from '@DesignSystem/modals';
import { useOrganizationUsers } from '@account-settings/api';
import { DSInformationIcon } from '@design-system/icons';
import { TextButton } from '@ds';
import Grid from '@material-ui/core/Grid';
import useUserPermission from '@shared/api/useUserPermission';
import {
  APPLICATION_TYPE,
  ORGANIZATION_PLAN_ENTERPRISE,
  PAYMENT_PLANS
} from '@shared/constants';
import { MANAGEMENT_PERMISSIONS } from '@shared/constants/permissions';
import {
  useBetaFeatureEnabled,
  useCurrentOrganization,
  useCurrentPaymentPlan,
  useDebouncedFilter,
  useIsLLMOnlyUser,
  useIsSelectedAppLLM
} from '@shared/hooks';
import { useCurrentUserContext } from '@shared/hooks/useCurrentUserContext';

import { UpgradeToInvite } from '../UpgradeToInvite';
import './InviteMembersModal.scss';
import { getInviteMembersModalState } from '../../../../reducers/ui/workspaceUiReducer';
import { useDispatch, useSelector } from 'react-redux';
import useInviteWorkspaceUsersMutation from '@API/workspace/useInviteWorkspaceUsersMutation';
import workspaceActions from '../../../../actions/workspaceActions';
import queryString from 'query-string';
import { useLocation } from 'react-router';
import { OrganizationPaymentPlan } from '@shared/types';
import { getActiveOrganizationId } from '@/reducers/ui/organizationsUiReducer';

const INCORRECT_EMAIL_ADRESS_MSG = 'Incorrect email address';

const checkValueToAdd = (value: string) => !!value.replaceAll(' ', '')?.length;

type Member = {
  username?: string;
  email: string;
};

const getPaymentPrice = (
  paymentPlan: OrganizationPaymentPlan | null,
  isFree: boolean
) => {
  const planPrice = paymentPlan && PAYMENT_PLANS?.[paymentPlan]?.price;
  if (isFree || !planPrice) {
    return 0;
  }

  return planPrice;
};

const InviteMembersModal = () => {
  const { workspaceName, workspaceId, open } = useSelector(
    getInviteMembersModalState
  );

  const { search } = useLocation();

  const isFromTrial = useMemo(() => {
    const { fromTrial = false } = queryString.parse(search);

    return !!fromTrial;
  }, [search]);

  const inviteMembersMutation = useInviteWorkspaceUsersMutation({
    workspaceId
  });

  const dispatch = useDispatch();

  const onClose = () => {
    dispatch(
      workspaceActions.setInviteMembersModalState({
        open: false,
        workspaceId,
        workspaceName
      })
    );
  };

  const onPrimaryButtonClick = async (
    users: string[],
    app: APPLICATION_TYPE
  ) => {
    try {
      await inviteMembersMutation.mutateAsync({ app, users } as never);
    } finally {
      onClose();
    }
  };

  const organization = useCurrentOrganization();
  const {
    isPossibleToInviteUsers: isPossibleToInviteUsersByPaymentPlan,
    isFree,
    isFreePlan,
    paymentPlanName
  } = useCurrentPaymentPlan();
  const paymentPrice = getPaymentPrice(paymentPlanName, isFree || isFreePlan);

  const isSelectedAppLLM = useIsSelectedAppLLM();
  const isLLMOnlyUser = useIsLLMOnlyUser();
  const [shouldDisplayLLM, setShouldDisplayLLM] = useState(
    isLLMOnlyUser || isSelectedAppLLM
  );
  const [membersValue, setMembersValue] = useState<string[]>([]);
  const {
    debouncedFilterValue,
    setFilterValue,
    filterValue
  } = useDebouncedFilter('');

  const { getPermissionStatus } = useUserPermission();
  const isPossibleToInviteOutsideOrganization = getPermissionStatus({
    permissionKey: MANAGEMENT_PERMISSIONS.INVITE_USERS_OUT_OF_ORGANIZATION,
    workspaceName
  });

  useEffect(() => {
    setShouldDisplayLLM(isLLMOnlyUser || isSelectedAppLLM);
  }, [isLLMOnlyUser, isSelectedAppLLM]);

  const getTitle = () => {
    if (isFromTrial) {
      return (
        <div className="invite-members-modal-title">
          Welcome to your 30-day Comet Trial!
        </div>
      );
    }

    return (
      <div className="invite-members-modal-title">
        <div>Invite your teammates to workspace</div>
        <div>{workspaceName}</div>
      </div>
    );
  };

  const getSecondaryButtonText = () =>
    isFromTrial ? 'Skip, I will do it later' : 'Cancel';

  const isLLMEvalEnabled = useBetaFeatureEnabled(BETA_FEATURE_LLM_EVAL);
  const currentOrganization = useCurrentOrganization();
  const { user: currentUser } = useCurrentUserContext();
  const { data: usernames = [] } = useInviteMembersList({
    filterValue: debouncedFilterValue,
    workspaceId,
    organizationId: currentOrganization?.id
  }) as { data?: Member[] };
  const { data: workspaceMembers = [] } = useWorkspaceMembers(workspaceId) as {
    data?: Member[];
  };

  const activeOrganizationId = useSelector(getActiveOrganizationId);

  const {
    data: allOrgUsers = [],
    isLoading: isLoadingUsers
  } = useOrganizationUsers(activeOrganizationId);

  const workspaceMembersUsernames = useMemo(
    () => workspaceMembers.map(({ username, email }) => username || email),
    [workspaceMembers]
  );
  const options = useMemo(
    () => usernames.map(username => ({ value: username })),
    [usernames]
  );
  const allOrgUsersUsernames = useMemo(
    () => allOrgUsers.map(({ username, email }) => username || email),
    [allOrgUsers]
  );

  useEffect(() => {
    if (!open) {
      setMembersValue([]);
      setFilterValue('');
    }
  }, [open, setFilterValue]);

  const handlePrimaryButtonClick = () => {
    const usernamesThatAreNotInWs: string[] = [];

    membersValue.reduce((acc, value) => {
      if (!workspaceMembersUsernames.includes(value)) {
        acc.push(value);
      }
      return acc;
    }, usernamesThatAreNotInWs);

    onPrimaryButtonClick(
      usernamesThatAreNotInWs,
      shouldDisplayLLM ? APPLICATION_TYPE.llm : APPLICATION_TYPE.em
    );
  };

  const handleInviteAllClick = useCallback(() => {
    setMembersValue(prevMembers =>
      uniq([...prevMembers, ...allOrgUsersUsernames])
    );
  }, [allOrgUsersUsernames]);

  const handleRemoveAllClick = () => setMembersValue([]);

  const renderMemberOption = (option: { value: string }) => {
    return option.value;
  };

  const isConsideredAsEmail = (value: string) => value.includes('@');

  const checkChipValidity = useCallback(value => {
    if (!isConsideredAsEmail(value)) {
      return true;
    }

    return isValidEmail(value);
  }, []);

  const allUsersAreInvited =
    membersValue.length === allOrgUsersUsernames.length;

  const renderAdditionalContent = useCallback(() => {
    return (
      <Grid
        container
        justifyContent="flex-end"
        alignContent="center"
        alignItems="center"
        className="invite-members-modal-add-all"
      >
        <Grid item xs="auto">
          <TextButton
            type="tertiary"
            onClick={handleInviteAllClick}
            disabled={
              isLoadingUsers ||
              inviteMembersMutation?.isLoading ||
              allUsersAreInvited
            }
          >
            Invite all organization users
          </TextButton>
        </Grid>
      </Grid>
    );
  }, [
    allUsersAreInvited,
    handleInviteAllClick,
    isLoadingUsers,
    inviteMembersMutation?.isLoading
  ]);

  const isValid = useMemo(() => membersValue.every(checkChipValidity), []);

  const renderChip = (chipOption: unknown, chipValue: string) => {
    if (
      isEmail(chipValue) &&
      isPossibleToInviteOutsideOrganization &&
      checkChipValidity(chipValue)
    ) {
      return `Invite “${chipValue}” by email`;
    }

    return chipValue;
  };

  const addCurrentValue = () => {
    if (!membersValue.includes(filterValue)) {
      setMembersValue(prevMembersValue => [...prevMembersValue, filterValue]);
    }

    setFilterValue('');
  };

  const renderNoItems = () => {
    if (!filterValue) {
      return <div className="invite-members-modal-no-items" />;
    }

    const byEmailMessage = filterValue.includes('@') ? ' by email' : '';

    return (
      <div className="invite-members-modal-no-items" onClick={addCurrentValue}>
        <span>+</span>Invite “{filterValue}”{byEmailMessage}
      </div>
    );
  };

  const renderBilling = () => {
    if (organization?.paymentPlan === ORGANIZATION_PLAN_ENTERPRISE) return null;

    if (shouldDisplayLLM) {
      return (
        <div className="invite-members-modal-billing">
          <DSInformationIcon />
          Users who are not part of your organization will be added as LLM
          Members ($0/month).
        </div>
      );
    }

    return (
      <div className="invite-members-modal-billing">
        <DSInformationIcon />
        Users who are not part of your organization will be added as Members ($
        {paymentPrice}/month).
      </div>
    );
  };

  const renderFooter = () => {
    if (!isLLMEvalEnabled || isLLMOnlyUser) return null;

    if (shouldDisplayLLM) {
      return (
        <div className="invite-members-modal-footer">
          <div
            className="invite-members-modal-footer-text"
            onClick={() => setShouldDisplayLLM(false)}
          >
            Do you want to invite teammates to Experiment management?
          </div>
        </div>
      );
    }

    return (
      <div className="invite-members-modal-footer">
        <div
          className="invite-members-modal-footer-text"
          onClick={() => setShouldDisplayLLM(true)}
        >
          Do you want to invite teammates to LLM evaluation (Opik)?
        </div>
      </div>
    );
  };

  const renderInviteMembers = () => {
    const inviteTypeLabel = currentUser?.sagemakerRestrictions
      ? 'Username'
      : 'Username/email';

    return (
      <div className="invite-members-modal-content">
        <img
          className="invite-member-picture"
          src={`/images/account-settings/invite-${
            isFromTrial ? 'trial-' : ''
          }members.svg`}
        />

        {isFromTrial && (
          <div className="trial-invitation-label-container">
            <p>Comet is better together!</p>
            <p>You can now start inviting your teammates</p>
          </div>
        )}

        <div className="autocomplete-members">
          <MultipleAutocomplete
            onClearAll={handleRemoveAllClick}
            clearAll
            noValuePlaceholder={`${inviteTypeLabel}, comma separated`}
            withValuePlaceholder={`Add more ${inviteTypeLabel.toLowerCase()}`}
            label={`Invite member by Comet ${inviteTypeLabel.toLowerCase()}`}
            options={options}
            separator={/[\s,]/}
            value={membersValue}
            onChangeValue={setMembersValue}
            filterValue={filterValue}
            onChangeFilterValue={setFilterValue}
            renderListItem={renderMemberOption}
            renderChipLabel={renderChip}
            checkValueToAdd={checkValueToAdd}
            checkChipValidity={checkChipValidity}
            errorLabel={isValid ? null : INCORRECT_EMAIL_ADRESS_MSG}
            noItemsStrategy={NO_ITEMS_STRATEGY.SHOW_MESSAGE_WITH_NO_EMPTY_INPUT}
            noItems={renderNoItems}
            autoFocus
            classes="invite-members-autocomplete list"
            renderAdditionalContent={renderAdditionalContent}
          />
        </div>

        {renderBilling()}
        {renderFooter()}
      </div>
    );
  };

  if (!shouldDisplayLLM && !isPossibleToInviteUsersByPaymentPlan) {
    return (
      <UpgradeToInvite
        open={open}
        onClose={onClose}
        onSwitchToLLM={() => setShouldDisplayLLM(true)}
      />
    );
  }

  return (
    <FullWidthBasicModal
      className="invite-members-modal"
      open={open}
      onClose={onClose}
      title={getTitle()}
      onPrimaryButtonClick={handlePrimaryButtonClick}
      primaryButtonText="Send Invite"
      secondaryButtonText={getSecondaryButtonText()}
      content={renderInviteMembers()}
      onSecondaryButtonClick={onClose}
      isPrimaryButtonDisabled={
        !membersValue.length || !isValid || inviteMembersMutation?.isLoading
      }
      // TS missing props
      variant={undefined}
      stepActiveIndex={undefined}
      stepAmount={undefined}
      isPrimaryButtonLoading={undefined}
      withoutFooter={undefined}
      footer={undefined}
      withBackButton={undefined}
      onBackButtonClick={undefined}
      customHeaderContent={undefined}
      headerTitle={undefined}
      titleMaskTest={undefined}
    />
  );
};

export default InviteMembersModal;
